<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center flex-wrap gap-2">
              <h6 class="mb-0">Asistencia</h6>
              <div class="ms-auto d-flex gap-2 flex-wrap">
                <button type="button" class="btn btn-outline-success btn-sm mb-0" @click="markAll('present')"
                  :disabled="saving || loading || counts.none === 0">
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                  Todos asistieron
                </button>
              </div>
            </div>
            <p class="text-xs text-secondary mb-0 mt-1">
              Cada cambio se guarda de inmediato. Las tareas de producción se pagan por días asistidos.
            </p>

            <div class="row g-2 align-items-end mt-2">
              <div class="col-md-3 mb-3">
                <label for="attendanceDate" class="mb-1">Fecha</label>
                <input type="date" id="attendanceDate" v-model="date" class="form-control" @change="fetchDay" />
              </div>
              <div class="col-md-4 mb-3">
                <label for="attendanceSearch" class="mb-1">Buscar</label>
                <input type="text" id="attendanceSearch" v-model.trim="search" class="form-control" placeholder="Nombre del usuario" />
              </div>
              <div class="col-md-5 mb-3 d-flex gap-2 flex-wrap justify-content-md-end">
                <span class="badge bg-gradient-success">Asistieron: {{ counts.present }}</span>
                <span class="badge bg-gradient-danger">Faltaron: {{ counts.absent }}</span>
                <span class="badge bg-gradient-warning">Permiso: {{ counts.permission }}</span>
                <span class="badge bg-gradient-secondary">Sin registrar: {{ counts.none }}</span>
              </div>
            </div>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="loading" class="text-center p-4">
              <div class="spinner-border text-primary" role="status"></div>
            </div>
            <div class="table-responsive p-0" v-else>
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Usuario</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th v-for="shift in shifts" :key="shift.label" class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">{{ shift.label }}</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Descuento por falta</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Observación</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in filteredRows" :key="row.id">
                    <td>
                      <div class="d-flex flex-column px-2 py-1">
                        <h6 class="mb-0 text-sm">{{ row.name }}</h6>
                        <span class="text-xs text-secondary">{{ row.payment == 1 ? 'Por tarea' : 'Mensualidad' }}</span>
                      </div>
                    </td>
                    <td class="align-middle text-center">
                      <div class="btn-group btn-group-sm" role="group">
                        <button v-for="option in statusOptions" :key="option.value" type="button"
                          class="btn mb-0"
                          :class="row.status === option.value ? 'btn-' + option.color : 'btn-outline-' + option.color"
                          :disabled="row.locked || !!row.saving"
                          @click="setStatus(row, option.value)">
                          <span v-if="row.saving === option.value" class="spinner-border spinner-border-sm" role="status"></span>
                          <template v-else>{{ option.label }}</template>
                        </button>
                      </div>
                      <div v-if="row.locked" class="text-xxs text-secondary mt-1">
                        <i class="fa fa-lock me-1"></i>Descontado en un pago
                      </div>
                    </td>
                    <td v-for="shift in shifts" :key="shift.label" class="align-middle">
                      <div v-for="time in shift.times" :key="time.field"
                        class="d-flex align-items-center gap-1 mx-auto mb-1" style="max-width: 200px;">
                        <span class="text-xxs text-secondary" style="width: 45px;">{{ time.label }}</span>
                        <div class="input-group input-group-sm flex-nowrap">
                          <input type="time" :value="row[time.field]" class="form-control form-control-sm"
                            :disabled="row.locked || !!row.saving || row.status === 'absent'"
                            @change="setTime(row, time.field, $event.target.value)" />
                          <button type="button" class="btn btn-outline-secondary mb-0 px-2" title="Marcar hora actual"
                            :disabled="row.locked || !!row.saving || row.status === 'absent'"
                            @click="setTime(row, time.field, currentTime())">
                            <i class="fa fa-clock"></i>
                          </button>
                        </div>
                      </div>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <span v-if="row.status === 'absent'" class="text-danger font-weight-bold">
                        -{{ formatMoney(row.locked ? row.deduction : row.absence_deduction) }}
                      </span>
                      <span v-else class="text-secondary">—</span>
                      <div v-if="row.status === 'absent' && !row.locked && !Number(row.absence_deduction)" class="text-xxs text-warning">
                        Configure el valor en la ficha del usuario
                      </div>
                    </td>
                    <td class="align-middle" style="min-width: 160px;">
                      <input type="text" :value="row.observation" class="form-control form-control-sm"
                        :disabled="row.locked || !!row.saving || !row.status" maxlength="255"
                        :placeholder="row.status ? 'Opcional' : 'Marque el estado primero'"
                        @change="setObservation(row, $event.target.value)" />
                    </td>
                  </tr>
                  <tr v-if="filteredRows.length === 0">
                    <td colspan="6" class="text-center text-xxs">No hay usuarios activos para registrar asistencia</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header pb-0">
            <h6 class="mb-0">Resumen del periodo</h6>
            <div class="row g-2 align-items-end mt-2">
              <div class="col-md-3 mb-3">
                <label for="summaryFrom" class="mb-1">Desde</label>
                <input type="date" id="summaryFrom" v-model="summaryFrom" class="form-control" @change="fetchSummary" />
              </div>
              <div class="col-md-3 mb-3">
                <label for="summaryTo" class="mb-1">Hasta</label>
                <input type="date" id="summaryTo" v-model="summaryTo" class="form-control" @change="fetchSummary" />
              </div>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Usuario</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Asistió</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Faltó</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Permiso</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total descuentos</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pendiente por descontar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in summary" :key="item.user_id">
                    <td><h6 class="mb-0 text-sm px-2">{{ item.user?.name }}</h6></td>
                    <td class="text-center text-sm">{{ Number(item.present_days) }}</td>
                    <td class="text-center text-sm text-danger">{{ Number(item.absent_days) }}</td>
                    <td class="text-center text-sm">{{ Number(item.permission_days) }}</td>
                    <td class="text-center text-sm">{{ formatMoney(item.total_deduction) }}</td>
                    <td class="text-center text-sm">{{ formatMoney(item.pending_deduction) }}</td>
                  </tr>
                  <tr v-if="summary.length === 0">
                    <td colspan="6" class="text-center text-xxs">No hay registros en el periodo</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

const today = () => new Date().toLocaleDateString('en-CA');
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
};

// Horas de entrada y salida de la jornada de la mañana y de la tarde
const TIME_FIELDS = ['morning_check_in', 'morning_check_out', 'afternoon_check_in', 'afternoon_check_out'];

export default {
  name: 'AttendanceTable',
  data() {
    return {
      date: today(),
      search: '',
      rows: [],
      loading: false,
      saving: false,
      summaryFrom: firstOfMonth(),
      summaryTo: today(),
      summary: [],
      statusOptions: [
        { value: 'present', label: 'Asistió', color: 'success' },
        { value: 'absent', label: 'Faltó', color: 'danger' },
        { value: 'permission', label: 'Permiso', color: 'warning' },
      ],
      shifts: [
        { label: 'Mañana', times: [
          { field: 'morning_check_in', label: 'Entrada' },
          { field: 'morning_check_out', label: 'Salida' },
        ] },
        { label: 'Tarde', times: [
          { field: 'afternoon_check_in', label: 'Entrada' },
          { field: 'afternoon_check_out', label: 'Salida' },
        ] },
      ],
    };
  },
  computed: {
    filteredRows() {
      const term = this.search.toLowerCase();
      return term ? this.rows.filter(r => r.name.toLowerCase().includes(term)) : this.rows;
    },
    counts() {
      const counts = { present: 0, absent: 0, permission: 0, none: 0 };
      this.rows.forEach(r => { counts[r.status || 'none']++; });
      return counts;
    },
  },
  methods: {
    formatMoney(value) {
      return parseFloat(value || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    async fetchDay() {
      if (!this.date) return;
      this.loading = true;
      try {
        const users = await this.$store.dispatch('attendance/getDay', { date: this.date });
        this.rows = users.map(u => ({
          ...u,
          status: u.attendance?.status || null,
          observation: u.attendance?.observation || '',
          deduction: u.attendance?.deduction || 0,
          ...Object.fromEntries(TIME_FIELDS.map(f => [f, u.attendance?.[f]?.slice(0, 5) || ''])),
          locked: !!u.attendance?.production_payment_id,
          saving: null,
        }));
      } catch (error) {
        Swal.fire('Error', error?.data?.message || 'No se pudo cargar la asistencia', 'error');
      } finally {
        this.loading = false;
      }
    },
    // Cada cambio de estado se guarda de inmediato; el permiso pide la observación
    async setStatus(row, status) {
      if (row.locked || row.status === status) return;

      let observation = row.observation;
      if (status === 'permission') {
        const result = await Swal.fire({
          title: 'Permiso',
          text: row.name,
          input: 'textarea',
          inputLabel: 'Observación',
          inputPlaceholder: 'Motivo del permiso',
          inputValue: row.observation || '',
          inputAttributes: { maxlength: 255 },
          showCancelButton: true,
          confirmButtonText: 'Guardar',
          cancelButtonText: 'Cancelar',
          inputValidator: (value) => (!value || !value.trim()) && 'Debe escribir la observación del permiso',
        });
        if (!result.isConfirmed) return;
        observation = result.value.trim();
      }

      const previous = { status: row.status, observation: row.observation, ...Object.fromEntries(TIME_FIELDS.map(f => [f, row[f]])) };
      Object.assign(row, { status, observation });
      // Una falta no tiene horas de entrada ni de salida
      if (status === 'absent') TIME_FIELDS.forEach(f => { row[f] = ''; });
      if (!(await this.saveRows([row]))) {
        Object.assign(row, previous);
      }
    },
    currentTime() {
      return new Date().toTimeString().slice(0, 5);
    },
    // Registrar una hora marca al usuario como presente si aún no tenía estado
    async setTime(row, field, time) {
      if (row.locked || row.status === 'absent') return;

      const previous = { status: row.status, [field]: row[field] };
      row[field] = time;
      if (!row.status) row.status = 'present';
      if (!(await this.saveRows([row]))) {
        Object.assign(row, previous);
      }
    },
    async setObservation(row, value) {
      const observation = value.trim();
      if (row.locked || !row.status || observation === (row.observation || '')) return;

      const previous = { observation: row.observation };
      row.observation = observation;
      if (!(await this.saveRows([row]))) {
        Object.assign(row, previous);
      }
    },
    async saveRows(rows) {
      rows.forEach(r => { r.saving = r.status; });
      const response = await this.$store.dispatch('attendance/saveDay', {
        date: this.date,
        records: rows.map(r => ({
          user_id: r.id,
          status: r.status,
          observation: r.observation || null,
          ...Object.fromEntries(TIME_FIELDS.map(f => [f, r[f] || null])),
        })),
      });
      rows.forEach(r => { r.saving = null; });

      if (response && response.status >= 200 && response.status < 300) {
        this.fetchSummary();
        return true;
      }
      const errors = response?.data?.errors ? Object.values(response.data.errors).flat().join('<br>') : '';
      Swal.fire({ icon: 'error', title: 'Error', html: errors || response?.data?.message || 'No se pudo guardar la asistencia' });
      return false;
    },
    // Marca y guarda a los usuarios que aún no tienen estado
    async markAll(status) {
      const pending = this.rows.filter(r => !r.locked && !r.status);
      if (pending.length === 0) return;

      pending.forEach(r => { r.status = status; });
      this.saving = true;
      const saved = await this.saveRows(pending);
      this.saving = false;
      if (!saved) pending.forEach(r => { r.status = null; });
    },
    async fetchSummary() {
      if (!this.summaryFrom || !this.summaryTo) return;
      try {
        this.summary = await this.$store.dispatch('attendance/getSummary', { from: this.summaryFrom, to: this.summaryTo });
      } catch (error) {
        this.summary = [];
      }
    },
  },
  mounted() {
    this.fetchDay();
    this.fetchSummary();
  },
};
</script>
